// Global Instructions Rule Applied!
//
// FundInvestorsTable — per-fund commitments grid for the Investors tab.
// Mirrors FundInvestmentSummaryTable's shape (cfg-driven columns, sub-key
// preferences, Edit Columns / Filtering / Sorting drawer) but with the
// Investors-specific dataset (one row per fund_commitment + per-currency
// total rows). "New Commitment" opens the New commitment form for this fund
// and row Edit opens the commitment's own form (the Commitment form, pocket
// rows included); Delete stays here. On a consolidated-fund member the
// button and the pocket rows' Transfer / Delete are disabled with a pointer
// to the consolidated fund's Investors tab. The
// GP column is a switch that saves on change (GridSwitchCell), on standalone
// and pocket rows alike. The investor name opens the Investor form (the
// investor ENTITY: name, legal name, classification, SBIC investor type, its
// additional notice documents), on pocket rows too; the parent account opens
// the account. The cfg "SBIC Investor Type" column is dropped client-side on
// funds that are not SBIC (globalConfigs.funds[].is_sbic).

import React, { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Button, Dropdown, Modal, Tooltip } from 'antd'
import { MoreOutlined } from '@ant-design/icons'
import { faPlus } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { toast } from 'react-toastify'
import type { RootState } from '../../../state_manager/store.ts'

import TableView from '../../../core/components/TableView/TableView.tsx'
import ViewToolbar, { RIBBON_CTA_CLASS } from '../../../core/components/ViewToolbar/index.tsx'
import RibbonButton from '../../../core/components/Button.jsx'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { ViewConfig } from '../../../core/components/ViewConfig/index.ts'
import type { ViewConfigColumn } from '../../../core/components/ViewConfig/types.ts'
import { createTableColumns } from '../../../ui_configs/table_grid_columns.ts'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import { useRefreshOnActivate } from '../../../core/hooks/useRefreshOnActivate.ts'
import {
  TABLE_ACCOUNTS,
  TABLE_COMMITMENTS,
  TABLE_FUND_TRANSACTIONS,
  TABLE_INVESTORS
} from '../../../state_manager/globals.ts'
import { recordHasTotalLabel, stripTotalTextFromCellNode } from '../../../core/utils/recordRowBold.ts'

import { useFundInvestors } from '../hooks/useFundInvestors.ts'
import { useFundInvestorsReportId } from '../../../state_manager/hooks/useFundInvestorsReportId.ts'
import { useFreezeLeadingColumns } from '../../../state_manager/hooks/useFreezeLeadingColumns.ts'
import { useReportingGridColumnsWithPreferences } from '../../../state_manager/hooks/useReportingGridColumnsWithPreferences.ts'
import { getReportingGridColumnsForReportId } from '../../../state_manager/utils/reportingGridColumns.ts'
import { getAddColumnsCatalogForReport } from '../../../state_manager/utils/reportingColumnCatalog.ts'
import { useFundCommitmentEditorOptions } from '../hooks/useFundCommitmentEditorOptions.ts'
import { saveFundCommitment, trashFundCommitment } from '../services/fundsService.ts'
import GridSwitchCell from '../../../core/components/GridSwitchCell.tsx'
import { fetchAllFundInvestorsForExport } from '../../shared/table_grid_export/fetchAllFilteredForExport.ts'
import { useReportingGridExport } from '../../shared/table_grid_export/useReportingGridExport.ts'
import HelpTip from '../../../core/components/HelpTip.tsx'

interface Props {
  fundId: string | null
  /** Called after a commitment is added, edited or deleted (Summary refresh). */
  onCommitmentsChanged?: () => void
}

const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null)

const SBIC_ONLY_COLUMN_KEYS = new Set(['sbic_investor_types__label'])
const GP_COLUMN_KEY = 'fund_commitments__is_gp_commitment'
const GP_COLUMN_HELP = "The general partner's own commitment. Printed on the ILPA workbook."

export function FundInvestorsTable({ fundId, onCommitmentsChanged }: Props): React.ReactElement | null {
  const { openFormInTab } = useTabManagerContext()
  // SBIC-only columns (SBIC investor type) are cfg-visible on the report but
  // meaningless on other funds - dropped here rather than per-fund cfg
  const isSbicFund = useSelector(
    (state: RootState) => (state.globalConfigs?.funds ?? []).find((f) => f?.id === fundId)?.is_sbic === true
  )
  const fundName = useSelector(
    (state: RootState) => (state.globalConfigs?.funds ?? []).find((f) => f?.id === fundId)?.name ?? 'Fund'
  )
  // static Modal.confirm does not render under React 19 - hook form required
  const [modalApi, modalContextHolder] = Modal.useModal()
  // the fund's context: whether it is a member of a consolidated fund
  const { options: editorOptions } = useFundCommitmentEditorOptions(fundId)
  const consolidatedFundName = editorOptions?.fund?.fund_groups__name ?? null
  // "Close Commitments" opens a Closing fund transaction pre-scoped to this
  // fund (cfg.fund_transaction_types.closes_commitments); booking it stamps
  // Closed + date_closed on the ticked commitments in one go
  const transactionTypes = useSelector((state: RootState) => state.globalConfigs?.fund_transaction_types ?? [])
  const closingType = useMemo(() => transactionTypes.find((t) => t.closes_commitments) ?? null, [transactionTypes])
  // row menu "Transfer…" opens the Transfer of Interest form prefilled
  const transferType = useMemo(() => transactionTypes.find((t) => t.transfers_interest) ?? null, [transactionTypes])

  const reportId = useFundInvestorsReportId()
  const freezeLeadingColumns = useFreezeLeadingColumns(reportId)
  const reportingConfigs = useSelector((s: RootState) => s.globalConfigs?.reporting_configs ?? [])
  const reportingColumnDefaults = useSelector((s: RootState) => s.globalConfigs?.reporting_column_defaults ?? [])
  const reportingColumns = useSelector((s: RootState) => s.globalConfigs?.reporting_columns ?? [])

  const {
    data,
    loading,
    filterOptions,
    filterOptionsLoading,
    activeFilters,
    activeSorting,
    hasMore,
    currentPage,
    preferenceRow,
    handleSetFilters,
    handleSetSorting,
    handleResetFilters,
    persistColumnFieldOrder,
    resetColumnLayoutToDefault,
    refetch,
    defaultTableFilters,
    defaultTableSorting
  } = useFundInvestors(fundId)

  const gridColumns = useReportingGridColumnsWithPreferences(reportId, preferenceRow)
  const viewConfigColumns: ViewConfigColumn[] = gridColumns

  const addColumnsCatalog = useMemo(
    () =>
      getAddColumnsCatalogForReport(
        reportId,
        {
          reporting_columns: reportingColumns,
          reporting_column_defaults: reportingColumnDefaults
        },
        { primaryModuleKey: 'investors' }
      ),
    [reportId, reportingColumns, reportingColumnDefaults]
  )

  const catalogFieldNameOrder = useMemo(
    () => [...addColumnsCatalog.deal, ...addColumnsCatalog.related].map((c) => c.valueKey),
    [addColumnsCatalog]
  )

  const defaultTableColumnKeys = useMemo(() => {
    if (!reportId) return null
    const rows = getReportingGridColumnsForReportId(reportId, {
      reporting_configs: reportingConfigs,
      reporting_column_defaults: reportingColumnDefaults,
      reporting_columns: reportingColumns
    })
    return rows.map((r) => r.valueKey)
  }, [reportId, reportingConfigs, reportingColumnDefaults, reportingColumns])

  const columnByValueKey = useMemo(() => {
    const m = new Map<string, ViewConfigColumn>()
    for (const c of addColumnsCatalog.deal) m.set(c.valueKey, c)
    for (const c of addColumnsCatalog.related) m.set(c.valueKey, c)
    for (const c of viewConfigColumns) {
      if (!m.has(c.valueKey)) m.set(c.valueKey, c)
    }
    return m
  }, [addColumnsCatalog, viewConfigColumns])

  const resolveViewConfigColumn = useCallback((valueKey: string) => columnByValueKey.get(valueKey), [columnByValueKey])

  const columnLayout = useMemo(
    () => ({
      onPersistFieldOrder: persistColumnFieldOrder,
      onResetToDefaults: resetColumnLayoutToDefault,
      resolveColumn: resolveViewConfigColumn,
      catalogFieldNameOrder,
      defaultTableColumnKeys
    }),
    [
      persistColumnFieldOrder,
      resetColumnLayoutToDefault,
      resolveViewConfigColumn,
      catalogFieldNameOrder,
      defaultTableColumnKeys
    ]
  )

  const visibleGridColumns = useMemo(
    () => (isSbicFund ? gridColumns : gridColumns.filter((c) => !SBIC_ONLY_COLUMN_KEYS.has(String(c.valueKey ?? '')))),
    [gridColumns, isSbicFund]
  )
  const fetchAllForExport = useCallback(
    (token: string | null) =>
      fundId
        ? fetchAllFundInvestorsForExport(fundId, activeSorting, activeFilters, token)
        : Promise.resolve({ data: [] }),
    [fundId, activeSorting, activeFilters]
  )
  // per-currency Total rows carry no commitment id
  const keepCommitmentRow = useCallback(
    (row: Record<string, unknown>) => typeof row.fund_commitments__id === 'string',
    []
  )
  const { exportLoading, handleExport } = useReportingGridExport({
    title: `${fundName} Investors`,
    gridColumns: visibleGridColumns,
    fetchAll: fetchAllForExport,
    keepRow: keepCommitmentRow
  })

  const { columns: baseColumns, frozenColumns } = useMemo(
    () => createTableColumns({ columns: visibleGridColumns, freezeLeadingColumns, fallbackRecordModuleKey: 'companies' }),
    [visibleGridColumns, freezeLeadingColumns]
  )

  const firstColumnValueKey = gridColumns[0]?.valueKey

  const afterChange = useCallback(() => {
    refetch()
    onCommitmentsChanged?.()
  }, [refetch, onCommitmentsChanged])
  // a commitment added or changed on its form, or an investor renamed on the
  // Investor form (each in its own tab), shows here when the user comes back
  const activateRef = useRefreshOnActivate(afterChange, { maxAgeMs: 0 })

  const handleDelete = useCallback(
    (target: { id: string; investorName: string }) => {
      modalApi.confirm({
        title: 'Delete commitment',
        content: `Delete ${target.investorName}'s commitment to this fund?`,
        okText: 'Delete',
        okButtonProps: { danger: true },
        onOk: async () => {
          const res = await trashFundCommitment(target.id, getAccessToken(store.getState().auth))
          if (res.success) {
            toast.success('Commitment deleted')
            afterChange()
          } else {
            showErrorToast(res.error || 'Failed to delete commitment')
          }
        }
      })
    },
    [modalApi, afterChange]
  )

  // the GP switch: saved on change, pocket rows included (the marker is the
  // pocket's own; fund__save_commitment takes id + is_gp_commitment on both)
  const saveGpCommitment = useCallback(
    async (commitmentId: string, next: boolean): Promise<boolean> => {
      const res = await saveFundCommitment(
        { id: commitmentId, is_gp_commitment: next },
        false,
        getAccessToken(store.getState().auth)
      )
      if (!res.success) {
        showErrorToast(res.error || 'Failed to save the GP commitment')
        return false
      }
      toast.success(next ? 'Marked as the GP commitment' : 'No longer the GP commitment')
      afterChange()
      return true
    },
    [afterChange]
  )

  const columns = useMemo(() => {
    const mapped = baseColumns.map((col) => {
      if (col.key === GP_COLUMN_KEY) {
        return {
          ...col,
          align: 'center' as const,
          title: (
            <span>
              {col.title}
              <HelpTip title={GP_COLUMN_HELP} />
            </span>
          ),
          render: (_: unknown, record: Record<string, unknown>) => {
            const commitmentId = str(record.fund_commitments__id)
            if (recordHasTotalLabel(record) || !commitmentId) return null
            return (
              <GridSwitchCell
                checked={record[GP_COLUMN_KEY] === true}
                ariaLabel='GP commitment'
                onSave={(next) => saveGpCommitment(commitmentId, next)}
              />
            )
          }
        }
      }
      const prevRender = col.render
      if (!prevRender) return col
      const isFirstColumn = firstColumnValueKey != null && col.key === firstColumnValueKey
      // the two record links: the investor name opens the Investor form, the
      // parent account the account form
      const isInvestorCol = col.key === 'investors__name'
      const isParentAccountCol = col.key === 'accounts__name'
      return {
        ...col,
        render: (value: unknown, record: Record<string, unknown>, index: number, editingContext: unknown) => {
          const node = prevRender!(value, record, index, editingContext)
          const isTotalRow = recordHasTotalLabel(record)
          const renderedNode = isTotalRow && !isFirstColumn ? stripTotalTextFromCellNode(node) : node

          if (isTotalRow || (!isParentAccountCol && !isInvestorCol)) return renderedNode

          const linkedId = str(isInvestorCol ? record.investors__id : record.accounts__id)
          if (!linkedId) return renderedNode

          return (
            <button
              type='button'
              className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
              onClick={() =>
                isInvestorCol
                  ? openFormInTab(TABLE_INVESTORS, linkedId, String(record.investors__name ?? ''))
                  : openFormInTab(TABLE_ACCOUNTS, linkedId, String(record.accounts__name ?? ''))
              }
            >
              {renderedNode}
            </button>
          )
        }
      }
    })

    mapped.push({
      title: '',
      key: '_actions',
      width: 48,
      fixed: 'right' as const,
      render: (_: unknown, record: Record<string, unknown>) => {
        const commitmentId = str(record.fund_commitments__id)
        if (recordHasTotalLabel(record) || !commitmentId) return null
        const isPocket = str(record.fund_commitments__fund_group_commitment_id) != null
        const statusLabel = str(record.fund_commitment_statuses__label)
        const isLive = statusLabel !== 'Transferred' && statusLabel !== 'Settled'
        const transferDisabledReason = isPocket
          ? "Managed on the consolidated fund's Investors tab"
          : !transferType
            ? 'No Transfer of Interest transaction type is configured'
            : !isLive
              ? `A ${statusLabel} commitment cannot be transferred`
              : null
        const transferLabel = transferDisabledReason ? (
          <Tooltip title={transferDisabledReason}>Transfer…</Tooltip>
        ) : (
          'Transfer…'
        )
        return (
          <Dropdown
            trigger={['click']}
            menu={{
              items: [
                // opens the Commitment form; a pocket's shows what is set on
                // the consolidated commitment and its own GP marker
                { key: 'edit', label: 'Edit' },
                { key: 'transfer', disabled: transferDisabledReason != null, label: transferLabel },
                { type: 'divider' as const },
                {
                  key: 'delete',
                  danger: true,
                  disabled: isPocket,
                  label: isPocket ? (
                    <Tooltip title="Deleted with its consolidated commitment, on the consolidated fund's Investors tab">
                      Delete
                    </Tooltip>
                  ) : (
                    'Delete'
                  )
                }
              ],
              onClick: ({ key }) => {
                const investorName = str(record.investors__name) ?? ''
                if (key === 'edit') openFormInTab(TABLE_COMMITMENTS, commitmentId, investorName)
                else if (key === 'transfer' && transferType && fundId)
                  openFormInTab(TABLE_FUND_TRANSACTIONS, null, 'Transfer of interest', {
                    fund: fundId,
                    type: String(transferType.id),
                    commitment: commitmentId
                  })
                else if (key === 'delete') handleDelete({ id: commitmentId, investorName })
              }
            }}
          >
            <Button type='text' size='small' icon={<MoreOutlined />} />
          </Dropdown>
        )
      }
    } as (typeof mapped)[number])

    return mapped
  }, [baseColumns, firstColumnValueKey, openFormInTab, handleDelete, transferType, fundId, saveGpCommitment])

  // Row id: fund_commitments.id is the only field that's truly unique per
  // row — investors can have multiple commitments to the same fund (different
  // tranches / closings) so investor id alone causes duplicate AntD keys
  // and stale-row reconciliation. Total rows get a synthetic per-currency key.
  const dataSource = useMemo(
    () =>
      data.map((row, i) => {
        const commitmentId = typeof row.fund_commitments__id === 'string' ? row.fund_commitments__id : ''
        const totalKey = `total-${typeof row.currency_code === 'string' ? row.currency_code : i}`
        const id = commitmentId || totalKey
        return { ...row, id, key: id }
      }),
    [data]
  )

  const currentFilters = useMemo(() => {
    if (!Array.isArray(activeFilters)) return activeFilters ?? {}
    return activeFilters.reduce((acc: Record<string, any>, f) => {
      acc[f.key] = f.value
      return acc
    }, {})
  }, [activeFilters])

  const currentSortConfig = useMemo(() => {
    if (!Array.isArray(activeSorting) || activeSorting.length === 0) return null
    const first = activeSorting[0]
    return { column: first.key, direction: first.direction }
  }, [activeSorting])

  if (!fundId) return null

  return (
    <div ref={activateRef} className='flex-1 min-h-0 h-full flex flex-col gap-2'>
      {modalContextHolder}
      <ViewToolbar
        moduleKey={TABLE_ACCOUNTS}
        recordTitleField='investors__name'
        addButtonEnabled={false}
        searchEnabled={false}
        variant='secondary'
        leftSlot={
          <div className='flex items-center gap-2 text-white'>
            <span className='w-2 h-2 rounded-full bg-indigo-300' />
            <span className='font-semibold text-sm'>Investors</span>
          </div>
        }
        activeFilters={activeFilters}
        onRefreshData={() => refetch()}
        onFilterChange={(filters) => handleSetFilters(Array.isArray(filters) ? filters : [])}
        defaultFilters={defaultTableFilters}
        onResetFilters={handleResetFilters}
        exportEnabled
        onExport={handleExport}
        exportLoading={exportLoading}
        beforeAddSlot={
          <div className='flex items-center gap-2'>
            <ViewConfig
              moduleTitle='Fund Investors'
              columns={viewConfigColumns}
              filterOptions={filterOptions}
              filterOptionsLoading={filterOptionsLoading}
              addColumnsCatalog={addColumnsCatalog}
              addColumnsTabLabels={{ primary: 'Investor', related: 'Related' }}
              columnLayout={columnLayout}
              filteringConfig={{
                currentFilters: activeFilters,
                defaultFilters: defaultTableFilters,
                onApplyFilters: async (filters) => {
                  handleSetFilters(filters)
                },
                onResetToDefaults: async () => {
                  handleSetFilters(defaultTableFilters)
                }
              }}
              sortingConfig={{
                currentSorting: activeSorting,
                defaultSorting: defaultTableSorting,
                onApplySorting: async (sorting) => {
                  handleSetSorting(sorting)
                },
                onResetToDefaults: async () => {
                  handleSetSorting(defaultTableSorting)
                }
              }}
            />
            <Tooltip
              title={
                consolidatedFundName
                  ? `Commitments to this fund are closed as consolidated commitments on ${consolidatedFundName}`
                  : !closingType
                    ? 'No Closing transaction type is configured'
                    : 'Close a batch of commitments as of one date (a Closing fund transaction)'
              }
            >
              <span>
                <RibbonButton
                  disabled={!fundId || !closingType || consolidatedFundName != null}
                  onClick={() =>
                    openFormInTab(TABLE_FUND_TRANSACTIONS, null, 'Closing', {
                      fund: fundId ?? '',
                      type: String(closingType?.id ?? '')
                    })
                  }
                >
                  Close Commitments
                </RibbonButton>
              </span>
            </Tooltip>
            <Tooltip
              title={
                consolidatedFundName
                  ? `Commitments to this fund are added on the Investors tab of the consolidated fund ${consolidatedFundName}`
                  : undefined
              }
            >
              <span>
                <RibbonButton
                  variant='primary'
                  icon={<FontAwesomeIcon icon={faPlus} />}
                  className={RIBBON_CTA_CLASS}
                  disabled={!editorOptions || consolidatedFundName != null}
                  onClick={() => openFormInTab(TABLE_COMMITMENTS, null, 'New Commitment', { fund: fundId })}
                >
                  New Commitment
                </RibbonButton>
              </span>
            </Tooltip>
          </div>
        }
      />

      <div className='flex-1 min-h-0 h-full' style={{ minHeight: 320 }}>
        <TableView
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          hasMore={hasMore}
          currentPage={currentPage}
          currentFilters={currentFilters}
          onFiltersChange={(filters) => {
            const filterArray = Object.entries(filters).map(([key, value]) => ({ key, value }))
            handleSetFilters(filterArray)
          }}
          onSortChange={(sortConfig: any) => {
            if (sortConfig) {
              handleSetSorting([{ key: sortConfig.column, direction: sortConfig.direction }])
            } else {
              handleSetSorting([])
            }
          }}
          filterOptions={filterOptions}
          filterOptionsLoading={filterOptionsLoading}
          currentSortConfig={currentSortConfig}
          frozenColumns={frozenColumns}
          totalsMode='sticky-bottom'
          rowClassName={(record) => (recordHasTotalLabel(record) ? 'font-bold' : undefined)}
          headerStyle={{ padding: '5px', minHeight: 36, backgroundColor: '#f5f5f5' }}
        />
      </div>

    </div>
  )
}

export default FundInvestorsTable
